import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  VStack,
  HStack,
  Avatar,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { Checkbox } from 'src/components/atoms/Checkbox';

type User = {
  id: string;
  name: string;
  avatarUrl?: string;
};

type MentionedUsersSelectProps = {
  options: User[];
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
};

export const MentionedUsersSelect: React.FC<MentionedUsersSelectProps> = ({
  options,
  selectedIds,
  onSelectionChange,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(
    undefined,
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((user) => user.name.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const selectedUsers = useMemo(() => {
    return options.filter((user) => selectedIds.has(user.id));
  }, [options, selectedIds]);

  const handleToggle = (userId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    onSelectionChange(newSelected);
  };

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);

  const displayValue =
    selectedUsers.length > 0 ? `${selectedUsers.length} selected` : '';

  return (
    <FormControl>
      <FormLabel
        fontFamily="marketingPage"
        fontWeight="medium"
        color="text.title"
        mb={2}>
        Mentioned Users
      </FormLabel>
      <Popover isOpen={isOpen} onClose={onClose} onOpen={onOpen} isLazy>
        <PopoverTrigger>
          <Box ref={triggerRef}>
            <InputGroup>
              <Input
                placeholder="Select users"
                value={displayValue}
                readOnly
                cursor="pointer"
                onClick={onOpen}
                border="1px solid"
                borderColor="border.box"
                borderRadius="md"
                fontFamily="marketingPage"
                _placeholder={{ color: 'gray.400' }}
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
              <InputRightElement pointerEvents="none">
                <Icon
                  iconName={isOpen ? 'chevron-up' : 'chevron-down'}
                  boxSize={4}
                  color="gray.400"
                />
              </InputRightElement>
            </InputGroup>
            {/* Show selected user avatars below input */}
            {selectedUsers.length > 0 && (
              <HStack mt={2} spacing={1}>
                {selectedUsers.slice(0, 5).map((user) => (
                  <Avatar
                    key={user.id}
                    size="xs"
                    name={user.name}
                    src={user.avatarUrl}
                  />
                ))}
                {selectedUsers.length > 5 && (
                  <Text fontSize="xs" color="gray.500">
                    +{selectedUsers.length - 5}
                  </Text>
                )}
              </HStack>
            )}
          </Box>
        </PopoverTrigger>
        <PopoverContent
          p={4}
          bg="surface.card"
          borderRadius="md"
          boxShadow="lg"
          w={triggerWidth ? `${triggerWidth}px` : '100%'}>
          {/* Search Input */}
          <Box mb={3}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon iconName="search" boxSize={4} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search users"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                border="1px solid"
                borderColor="border.box"
                borderRadius="md"
                pl={10}
                fontFamily="marketingPage"
              />
            </InputGroup>
          </Box>

          {/* Users List */}
          {filteredOptions.length > 0 && (
            <Text fontSize="xs" color="green.500" fontWeight="medium" mb={2}>
              People
            </Text>
          )}
          <Box maxH="200px" overflowY="auto">
            <VStack align="stretch" spacing={1}>
              {filteredOptions.map((user) => (
                <HStack
                  key={user.id}
                  p={2}
                  borderRadius="md"
                  _hover={{ bg: 'surface.hover' }}
                  cursor="pointer"
                  onClick={() => handleToggle(user.id)}>
                  <Checkbox
                    isChecked={selectedIds.has(user.id)}
                    pointerEvents="none"
                  />
                  <Avatar size="sm" name={user.name} src={user.avatarUrl} />
                  <Text fontSize="sm" fontFamily="marketingPage">
                    {user.name}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};
